import * as GeoJSON from 'geojson'
import mapboxgl, * as Mapbox from 'mapbox-gl'
import { Subscription } from 'rxjs'
import { map, startWith } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

import { Injectable } from '@angular/core'
import { AuthenticationService } from '@services/authentication.service'
import { CommandService } from '@services/command.service'
import { features$, FeatureService } from '@services/feature.service'
import { FileReferenceService } from '@services/file-reference.service'
import { FilterService } from '@services/filter.service'
import { PointerService } from '@services/pointer.service'
import { ProjectService } from '@services/project.service'
import { SceneService } from '@services/scene.service'
import { MarkerTypes } from '@services/spatial-annotation.service'
import { calculateStatistic, MathOperation, normalizeValues, simpleHash } from '@utils/Objects'
import { createIconSVGString, svgToCanvas, TagOptions } from '@utils/TagFeatures'

import { Feature, FeatureTypes, isMapFeature } from './Feature'
import { map$ } from './MapSpace'

export type DataPoint = {
    coordinates: number[]
    featureID: number
    key: string
    value: number
}

interface MarkerGeoJsonProperties extends GeoJSON.GeoJsonProperties {
    anchor: string
    backgroundColor: string
    backgroundShape: string
    displayType: MarkerTypes
    featureName: string
    fileReferenceID?: number
    fontColor: string
    hash?: string
    icon?: string
    imageName: string
    interacted: boolean
    opacity: number
    origin: "karta"
    position: number[]
    s3ObjectReferenceId?: number
    showText: boolean
    size: number
    textContent: string
    type: "marker"
    visibility: boolean
}

function getLineGeometry(coordinates: GeoJSON.Position[]): GeoJSON.LineString {
    return { 'type': 'LineString', 'coordinates': coordinates }
}

function getPointGeometry(coordinates: GeoJSON.Position): GeoJSON.Point {
    return { 'type': 'Point', 'coordinates': coordinates }
}

function getPolygonGeometry(coordinates: GeoJSON.Position[]): GeoJSON.Polygon {
    return { 'type': 'Polygon', 'coordinates': [coordinates] }
}

/**
 * Feature to Mapbox flow:
 * - Define geometry/properties of the feature
 * - Convert to GeoJSON format
 * - Create/update the Mapbox source & layer
 * - Add to/update the map
 **/
@Injectable({
    providedIn: 'root'
})
export class MapManager {
    private _authorizationToken?: string
    private _map: Mapbox.Map
    private _markerImageLoadMap = new Map<string, {
        map: Mapbox.Map,
        styleVersion: number,
        promise: Promise<HTMLImageElement | ImageBitmap | ImageData>,
    }>()
    private _markerImageRestoreAttempts = new Set<string>()
    private _pendingRasterUpdate?: {
        map: Mapbox.Map,
        rasterImages: Feature[],
        styleVersion: number,
    }
    private _restoringStyleFeatures = false
    private _styleRestorePending = false
    private _styleReady = false
    private _styleVersion = 0
    private _subscriptions: Subscription[] = []
    public mapFeatures$ = features$.pipe(
        map(features => features.filter(feature => isMapFeature(feature)))
    )
    public geoJsonSourceMap = new Map<string, GeoJSON.Feature[]>()
    public initialCenter: mapboxgl.LngLat
    public markerImageMap = new Map<string, string>()
    public popup: mapboxgl.Popup
    public rasterSourceMap = new Map<string, Feature>()
    public styleLoaded = false
    public settingRaster = false

    get lines() { return this.geoJsonSourceMap.get('line') }
    get authorizationHeader() { return this._authorizationToken ? `Bearer ${this._authorizationToken}` : undefined }
    get mapFeatures() { return this.featureService.features.filter(feature => isMapFeature(feature)) }
    get markers() { return this.geoJsonSourceMap.get('pointSource') as GeoJSON.Feature<GeoJSON.Point, MarkerGeoJsonProperties>[] }
    get polygons() { return this.geoJsonSourceMap.get('polygon') }
    get rasterImages() { return this.mapFeatures.filter(feature => feature.type == 'rasterImage') }
    get selectedFeatureID() { return this.featureService.selectedFeatureID }

    constructor(
        private _commandService: CommandService,
        private _fileReferenceService: FileReferenceService,
        public authenticationService: AuthenticationService,
        public featureService: FeatureService,
        public filterService: FilterService,
        public pointerService: PointerService,
        public projectService: ProjectService,
        public sceneService: SceneService,
    ) {
        this._subscriptions.push(
            map$.subscribe(map => {
                this._map = map
                this._styleReady = false
                this._styleVersion++
                this._markerImageRestoreAttempts.clear()
                this.styleLoaded = false

                this.initialCenter = map.getCenter()

                // setStyle drops every custom source, layer, and runtime image. Restore centrally
                // from the live caches so deleted features stay deleted and new features return.
                // Do not add per-source style.load callbacks that capture stale data.
                map.on('style.load', async () => {
                    if (!this._syncStyleLoaded(map)) return

                    const styleVersion = this._styleVersion
                    await this._restoreStyleFeatures(map, styleVersion)
                })

                map.on('styledata', async () => {
                    if (!this._syncStyleLoaded(map)) return

                    const styleVersion = this._styleVersion
                    if (this._styleFeaturesNeedRestore(map, styleVersion)) {
                        await this._restoreStyleFeatures(map, styleVersion)
                    }
                })

                if (this.authenticationService.currentModule == 'builder') { // Line Vertice Edit
                    const JOINT_POINTS_SOURCE_ID = 'joint-points-source'
                    const JOINT_POINTS_LAYER_ID = 'joint-points-layer'

                    this._map.on('load', () => {
                        let draggedPointIndex: number | null = null
                        let isDragging = false

                        // Throttle logic
                        let lastUpdateTime = 0
                        const throttleInterval = 5

                        // 1) Function to remove existing joint-points layer+source safely
                        const removeJointPoints = () => {
                            try {
                                // Check if map is fully initialized
                                if (!this._map || !this._map.loaded() || !this.styleLoaded) return

                                // Get the current style layers
                                const style = this._map.getStyle()
                                if (!style || !style.layers) return

                                // Check if our layer exists in the style
                                const layerExists = style.layers.some(layer => layer.id === JOINT_POINTS_LAYER_ID)
                                const sourceExists = style.layers.some(layer => layer.source === JOINT_POINTS_SOURCE_ID)

                                // Only attempt removal if the layer/source exists
                                if (layerExists) {
                                    this._map.removeLayer(JOINT_POINTS_LAYER_ID)
                                }

                                if (sourceExists) {
                                    this._map.removeSource(JOINT_POINTS_SOURCE_ID)
                                }
                            } catch (err) {
                                console.error('[MapManager] Error removing joint points:', err)
                            }
                        }

                        // 2) Function to add or update the joint points for a given feature
                        const updateJointPoints = (feature: GeoJSON.Feature<GeoJSON.LineString | GeoJSON.Polygon>) => {
                            // First remove any existing layer/source
                            removeJointPoints()

                            let coordinates: number[][]
                            if (feature.geometry.type === 'Polygon') {
                                // Use outer ring
                                coordinates = feature.geometry.coordinates[0]
                                // For a polygon, remove the last point if it's identical to the first
                                if (coordinates.length > 1) {
                                    const [firstLng, firstLat] = coordinates[0]
                                    const [lastLng, lastLat] = coordinates[coordinates.length - 1]
                                    if (firstLng === lastLng && firstLat === lastLat) {
                                        coordinates = coordinates.slice(0, -1)
                                    }
                                }
                            } else if (feature.geometry.type === 'LineString') {
                                coordinates = feature.geometry.coordinates
                            } else {
                                console.error('[MapManager] Unsupported geometry:', feature.geometry['type'])
                                return
                            }

                            // Create point features
                            const pointFeatures = coordinates.map((coord, index) => ({
                                type: 'Feature' as const,
                                geometry: { type: 'Point' as const, coordinates: coord },
                                properties: { index, type: 'jointPoint' }
                            }))

                            // Check if source already exists
                            const existingSource = this._map.getSource(JOINT_POINTS_SOURCE_ID) as mapboxgl.GeoJSONSource
                            if (existingSource) {
                                // Update existing source
                                existingSource.setData({
                                    type: 'FeatureCollection',
                                    features: pointFeatures
                                })
                            } else {
                                // Add new source
                                this._map.addSource(JOINT_POINTS_SOURCE_ID, {
                                    type: 'geojson',
                                    data: {
                                        type: 'FeatureCollection',
                                        features: pointFeatures
                                    }
                                })
                            }

                            // Check if layer already exists
                            const existingLayer = this._map.getLayer(JOINT_POINTS_LAYER_ID)
                            if (existingLayer) {
                                // Layer exists, no need to add it again
                                return
                            }

                            // Add the layer only if it doesn't exist
                            this._map.addLayer({
                                id: JOINT_POINTS_LAYER_ID,
                                type: 'circle',
                                source: JOINT_POINTS_SOURCE_ID,
                                paint: {
                                    'circle-radius': 6,
                                    'circle-color': '#ffffff',
                                    'circle-stroke-width': 2,
                                    'circle-stroke-color': '#000000'
                                }
                            })
                        }

                        // 3) Subscribe to selectedFeature$ changes
                        //    NOTE: we want to handle null separately from distinctUntilKeyChanged('id').
                        this.featureService.selectedFeature$.subscribe(feature => {
                            // If no feature => remove points
                            if (!feature) {
                                removeJointPoints()
                                return
                            }
                            // If not line or polygon => remove points
                            if (feature.type !== 'line' && feature.type !== 'polygon') {
                                removeJointPoints()
                                return
                            }
                            // Otherwise, retrieve from geoJson
                            const featureIDString = String(feature.id)
                            const sourceID = this._findGeoJsonSource(featureIDString)
                            if (!sourceID) {
                                console.error('[MapManager] No source ID for feature', feature.id)
                                return
                            }
                            const geoJsonFeatures = this.geoJsonSourceMap.get(sourceID)
                            if (!geoJsonFeatures) {
                                console.error('[MapManager] No GeoJSON features for source', sourceID)
                                return
                            }
                            const sourceFeature = geoJsonFeatures.find(f => String(f.id) === featureIDString) as GeoJSON.Feature<GeoJSON.LineString | GeoJSON.Polygon>
                            if (!sourceFeature) {
                                console.error('[MapManager] Could not find feature in source', feature.id)
                                return
                            }
                            // Show joint points
                            updateJointPoints(sourceFeature)
                        })

                        // 4) Handle mousedown => check if we are clicking on a joint point
                        this._map.on('mousedown', (e) => {
                            if (!this.featureService.selectedFeatureID) return

                            // Check if the joint points layer exists before querying
                            if (!this._map.getLayer(JOINT_POINTS_LAYER_ID)) return

                            const features = this._map.queryRenderedFeatures(e.point, { layers: [JOINT_POINTS_LAYER_ID] })
                            if (!features.length) return  // didn't click a joint

                            const clickedFeature = features[0]
                            draggedPointIndex = clickedFeature.properties?.index
                            if (typeof draggedPointIndex === 'number') {
                                isDragging = true
                                this.pointerService.setPointerState('grabbing', this._map.getCanvas().style)
                                this._map.dragPan.disable()
                            }
                        })

                        // 5) Handle mousemove => drag the point
                        this._map.on('mousemove', e => {
                            // If we're not dragging or don't have a dragged point index, do nothing
                            if (!isDragging || draggedPointIndex === null) return

                            // Throttle the mousemove
                            const now = Date.now()
                            if (now - lastUpdateTime < throttleInterval) return
                            lastUpdateTime = now

                            // Make sure there's a selected feature
                            const featureID = this.featureService.selectedFeatureID
                            if (!featureID) return

                            const featureIDString = String(featureID)
                            const sourceID = this._findGeoJsonSource(featureIDString)
                            if (!sourceID) return

                            const geoJsonFeatures = this.geoJsonSourceMap.get(sourceID)
                            if (!geoJsonFeatures) return

                            const sourceFeature = geoJsonFeatures.find(f => String(f.id) === featureIDString) as GeoJSON.Feature<GeoJSON.LineString | GeoJSON.Polygon>
                            if (!sourceFeature) return

                            // We'll use a small "pixelBuffer" for snapping logic
                            const pixelBuffer = 10
                            const bounds = [
                                [e.point.x - pixelBuffer, e.point.y - pixelBuffer] as [number, number],
                                [e.point.x + pixelBuffer, e.point.y + pixelBuffer] as [number, number]
                            ]
                            const [minLngLat, maxLngLat] = [this._map.unproject(bounds[0]), this._map.unproject(bounds[1])]

                            // Calculate a rough "max distance" to snap within
                            let minDistance = this.getDistance(minLngLat.lng, minLngLat.lat, maxLngLat.lng, maxLngLat.lat)

                            // Current mouse position
                            let coords: [number, number] = [e.lngLat.lng, e.lngLat.lat]
                            let [lng, lat] = coords

                            // Attempt to find a snap-to position among featurePositions
                            let closestPosition: number[] | null = null

                            for (const [id, position] of this.featureService.featurePositions) {
                                // Skip snapping to our own feature
                                if (String(id) === featureIDString) continue

                                // 'position' can be a single coordinate or an array of coordinates
                                if (typeof position[0] === 'number') {
                                    // Single coordinate
                                    const [posLng, posLat] = position
                                    const distance = this.getDistance(lng, lat, posLng, posLat)
                                    if (distance <= minDistance) {
                                        minDistance = distance
                                        closestPosition = [posLng, posLat]
                                    }
                                } else {
                                    // 'position' is an array of coordinates
                                    position.forEach(p => {
                                        const [posLng, posLat] = p as any
                                        const distance = this.getDistance(lng, lat, posLng, posLat)
                                        if (distance <= minDistance) {
                                            minDistance = distance
                                            closestPosition = [posLng, posLat]
                                        }
                                    })
                                }
                            }

                            // Update the geometry's coordinates
                            let updatedCoords: number[][]
                            if (sourceFeature.geometry.type === 'Polygon') {
                                updatedCoords = sourceFeature.geometry.coordinates[0]
                            } else {
                                updatedCoords = sourceFeature.geometry.coordinates
                            }

                            // If snapping is applicable, use closestPosition; otherwise use raw mouse coords
                            updatedCoords[draggedPointIndex] = closestPosition || coords

                            // Keep polygons closed if we're moving the first vertex
                            if (sourceFeature.geometry.type === 'Polygon') {
                                if (draggedPointIndex === 0 && updatedCoords.length > 1) {
                                    updatedCoords[updatedCoords.length - 1] = [...updatedCoords[0]]
                                }
                                sourceFeature.geometry.coordinates[0] = updatedCoords
                            } else {
                                sourceFeature.geometry.coordinates = updatedCoords
                            }

                            // Push updated geometry back into the map data
                            this._updateSourceData(sourceID, geoJsonFeatures)

                            // Re-draw the draggable joint points so they track the updated geometry
                            updateJointPoints(sourceFeature)
                        })

                        // 6) Handle mouseup => finish drag
                        this._map.on('mouseup', () => {
                            if (!isDragging) return
                            isDragging = false
                            draggedPointIndex = null
                            this.pointerService.setPointerState('grab', this._map.getCanvas().style)
                            this._map.dragPan.enable()

                            // Now persist the new coordinates (e.g. update property with JSON string)
                            const featureID = this.featureService.selectedFeatureID
                            if (!featureID) return

                            const featureIDString = String(featureID)
                            const sourceID = this._findGeoJsonSource(featureIDString)
                            if (!sourceID) return

                            const geoJsonFeatures = this.geoJsonSourceMap.get(sourceID)
                            if (!geoJsonFeatures || !this.featureService.selectedFeature) return

                            const sourceFeature = geoJsonFeatures.find(f => String(f.id) === featureIDString) as GeoJSON.Feature<GeoJSON.LineString | GeoJSON.Polygon>
                            if (!sourceFeature) return

                            // Flatten logic if needed
                            let updatedCoordinates: number[][]
                            if (sourceFeature.geometry.type === 'Polygon') {
                                updatedCoordinates = sourceFeature.geometry.coordinates[0] as any
                                // (If it is closed, the last vertex = the first)
                            } else {
                                updatedCoordinates = sourceFeature.geometry.coordinates as any
                            }

                            // Do the property update
                            let coorProperty = this.featureService.selectedFeature.properties.find(p => p.key === 'coordinateString')
                            if (coorProperty) {
                                coorProperty.value = JSON.stringify(updatedCoordinates)
                                this._commandService.update.featureProperty(coorProperty).subscribe()
                                this.featureService.featurePositions.set(featureID, updatedCoordinates as any)
                            }

                            // Re-update source data just to be safe
                            this._updateSourceData(sourceID, geoJsonFeatures)
                            // And re-draw
                            updateJointPoints(sourceFeature)
                        })

                        // 7) Handle click => if not clicking on a feature, remove the joint points
                        this._map.on('click', (e) => {
                            try {
                                // Suppose your line/polygon layers are named "line" and "polygon"
                                const layers = ['line', 'polygon'].filter(layer => this._map.getLayer(layer))
                                if (!layers.length) return

                                const features = this._map.queryRenderedFeatures(e.point, { layers })
                                if (!features.length) {
                                    // If no line/polygon is clicked, remove the joints
                                    removeJointPoints()
                                }
                            } catch (error) {
                                console.error('[MapManager] Error handling click:', error)
                            }
                        })
                    })
                }

                // Mapbox can evict runtime marker sprites independently of pointSource. Keep this
                // generation-scoped fallback even though normal style restoration preloads images.
                map.on('styleimagemissing', (event) => {
                    if (this._map !== map) return
                    const imageID = event['id']
                    const styleVersion = this._styleVersion
                    const attemptKey = `${styleVersion}:${imageID}`
                    if (this._markerImageRestoreAttempts.has(attemptKey)) return
                    this._markerImageRestoreAttempts.add(attemptKey)
                    const url = this.markerImageMap.get(imageID)

                    if (map.hasImage(imageID)) {
                        map.removeImage(imageID)
                    }

                    map.loadImage(url, (error, image) => {
                        if (this._map !== map || this._styleVersion !== styleVersion) return
                        if (!error) {
                            try {
                                if (!map.hasImage(imageID)) map.addImage(imageID, image)
                                this._markerImageRestoreAttempts.delete(attemptKey)
                            } catch (addImageError) {
                                console.warn("Error adding image:", addImageError)
                            }
                        } else {
                            // Only log error if it's not a known invalid image name
                            if (imageID !== 'ee' && imageID !== 'undefined' && imageID !== 'null') {
                                console.warn("Error loading image:", error)
                            }
                        }
                    })
                })
            })
        )
    }

    getDistance(lng1: number, lat1: number, lng2: number, lat2: number): number {
        const R = 6371e3 // Earth radius in meters
        const rad = Math.PI / 180
        const dLat = (lat2 - lat1) * rad
        const dLng = (lng2 - lng1) * rad

        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * rad) *
            Math.cos(lat2 * rad) *
            Math.sin(dLng / 2) *
            Math.sin(dLng / 2)

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

        return R * c // Distance in meters
    }

    addDataPointsToGrid(dataPoints: DataPoint[], operation: MathOperation = 'average', options: { gridSize: number, firstColor: string, secondColor: string, betweenColor: Function }) {
        const zoom = Math.ceil(this._map.getZoom())
        const grid = new Map<string, { coordinates: [number, number], values: number[] }>()
        const earthRadiusKm = 6371 // Earth's radius in kilometers
        const kmPerDegLat = 2 * Math.PI * earthRadiusKm / 360 // Kilometers per degree of latitude
        const scaleFactor = 1.5
        const kmPerDegLonOrigin = kmPerDegLat * Math.cos(this.initialCenter.lat * Math.PI / 180)
        const latGridSize = options.gridSize * Math.pow(scaleFactor, 22 - zoom)
        const lngGridSize = latGridSize * (kmPerDegLat / kmPerDegLonOrigin)
        // Small padding for visibility
        const latPadding = latGridSize * 0.05
        const lngPadding = lngGridSize * 0.05

        // Associate data points with grid cells
        dataPoints.forEach(({ value, coordinates }) => {
            const [longitude, latitude] = coordinates
            const roundedLon = Math.floor(longitude / lngGridSize) * lngGridSize
            const roundedLat = Math.floor(latitude / latGridSize) * latGridSize
            const key = `${roundedLon},${roundedLat}`

            if (!grid.has(key)) {
                grid.set(key, { coordinates: [roundedLon, roundedLat], values: [] })
            }
            grid.get(key).values.push(value)
        })

        // Calculate the statistic for each grid cell
        const statisticValues = Array.from(grid, ([, { coordinates, values }]) => calculateStatistic(values, operation))
        const min = Math.min(...statisticValues)
        const max = Math.max(...statisticValues)

        const zoomToAltMap: { [key: number]: number } = {
            1: 53500000, 2: 9588000, 3: 4400900, 4: 2802000, 5: 1569400, 6: 830000, 7: 422000, 8: 215750, 9: 108600, 10: 54000,
            11: 27550, 12: 13570, 13: 6875, 14: 3415, 15: 1700, 16: 870, 17: 435, 18: 225, 19: 120, 20: 65, 21: 40, 22: 26
        }
        const maxHeight = (zoomToAltMap[zoom] || zoomToAltMap[22]) / 2

        function getColorFromNormalizedValue(normalizedValue) {
            return options.betweenColor(options.firstColor, options.secondColor, normalizedValue)
        }

        const features = Array.from(grid, ([, { coordinates, values }]) => {
            const [longitude, latitude] = coordinates

            const statisticValue = calculateStatistic(values, operation)
            const [normalizedStatistic] = normalizeValues([statisticValue], { min, max })
            const barHeight = normalizedStatistic * maxHeight // Scales to max height based on normalized value
            const color = getColorFromNormalizedValue(normalizedStatistic)

            return {
                type: "Feature",
                properties: {
                    height: barHeight,
                    value: statisticValue,
                    color: color
                },
                geometry: {
                    type: "Polygon",
                    coordinates: [[
                        [longitude + lngPadding, latitude + latPadding],
                        [longitude + lngGridSize - lngPadding, latitude + latPadding],
                        [longitude + lngGridSize - lngPadding, latitude + latGridSize - latPadding],
                        [longitude + lngPadding, latitude + latGridSize - latPadding],
                        [longitude + lngPadding, latitude + latPadding] // Closing the polygon
                    ]]
                }
            } as GeoJSON.Feature<GeoJSON.Polygon, GeoJSON.GeoJsonProperties>
        })

        if (!this._map.getSource('data-grid-source')) {
            this._createDataGridSource()
        }

        const data = { 'type': "FeatureCollection", 'features': features } as GeoJSON.FeatureCollection
        const source = this._map.getSource('data-grid-source') as Mapbox.GeoJSONSource

        source.setData(data)
        this._map.moveLayer('grid-extrusion')

        return { min, max }
    }

    private _syncStyleLoaded(map: Mapbox.Map) {
        if (this._map !== map) return false

        const styleLoaded = map.isStyleLoaded()
        if (!styleLoaded) {
            // isStyleLoaded() is also false while ordinary custom sources load. base-layer is our
            // generation sentinel: setStyle removes it, source updates do not. Only its absence may
            // invalidate in-flight marker and raster restoration.
            if (!map.getLayer('base-layer')) {
                this._styleReady = false
                this.styleLoaded = false
            }
            return false
        }

        if (!this._styleReady || !this.styleLoaded) {
            this._styleVersion++
            this._markerImageRestoreAttempts.clear()
        }
        this._styleReady = true
        this.styleLoaded = true

        return true
    }

    private _isCurrentStyle(map: Mapbox.Map, styleVersion: number) {
        return this._map === map &&
            this._styleVersion === styleVersion &&
            this.styleLoaded &&
            this._styleReady
    }

    public restoreMapFeatureLayerOrder(map = this._map) {
        if (this._map !== map) return

        // Mapbox and Three share one WebGL stack. Preserve the functional order
        // polygon/outlinepolygon < 3d-models < line < pointLayer after every restore.
        // Use anchors, not the absolute top, so interaction layers keep their existing order.
        if (map.getLayer('line') && map.getLayer('pointLayer')) map.moveLayer('line', 'pointLayer')
        if (map.getLayer('3d-models')) {
            if (map.getLayer('line')) map.moveLayer('3d-models', 'line')
            if (map.getLayer('polygon')) map.moveLayer('polygon', '3d-models')
            if (map.getLayer('outlinepolygon')) map.moveLayer('outlinepolygon', '3d-models')
        }
    }

    private _styleFeaturesNeedRestore(map = this._map, styleVersion = this._styleVersion) {
        if (!this._isCurrentStyle(map, styleVersion)) return false
        if (!map.getLayer('base-layer')) return true
        if (this.lines?.length && (!map.getSource('line') || !map.getLayer('line'))) return true
        if (this.polygons?.length && (
            !map.getSource('polygon') ||
            !map.getLayer('polygon') ||
            !map.getLayer('outlinepolygon')
        )) return true
        if (this.markers?.length) {
            if (!map.getSource('pointSource') || !map.getLayer('pointLayer')) return true
            if (this.markers.some(marker => {
                const imageName = marker.properties.imageName
                const attemptKey = `${styleVersion}:${imageName}`
                return imageName && !map.hasImage(imageName) && !this._markerImageRestoreAttempts.has(attemptKey)
            })) return true
        }

        return this.rasterImages.some(image => {
            const sourceID = `tileset-${image.id}`
            return !map.getSource(sourceID) || !map.getLayer(sourceID)
        })
    }

    private async _restoreStyleFeatures(map = this._map, styleVersion = this._styleVersion, allowRetry = true) {
        if (!this._isCurrentStyle(map, styleVersion)) return
        if (this._restoringStyleFeatures) {
            this._styleRestorePending = true
            return
        }
        this._restoringStyleFeatures = true

        try {
            if (!map.getLayer('base-layer')) {
                map.addLayer({ id: 'base-layer', type: 'line', source: { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } })
            }

            if (this.lines?.length) {
                if (!map.getSource('line')) {
                    const data = { 'type': "FeatureCollection", 'features': this.lines } as GeoJSON.FeatureCollection
                    const source = { type: 'geojson', data } as Mapbox.SourceSpecification

                    this._addSourceToMap('line', source, 'line')
                } else if (!map.getLayer('line')) {
                    this._createLineLayer()
                }
            }

            if (this.polygons?.length) {
                if (!map.getSource('polygon')) {
                    const data = { 'type': "FeatureCollection", 'features': this.polygons } as GeoJSON.FeatureCollection
                    const source = { type: 'geojson', data } as Mapbox.SourceSpecification

                    this._addSourceToMap('polygon', source, 'polygon')
                } else if (!map.getLayer('polygon') || !map.getLayer('outlinepolygon')) {
                    this._createPolygonLayer()
                }
            }

            this.restoreMapFeatureLayerOrder(map)

            await this._setMarkerSourceData(this.markers ?? [], map, styleVersion)
            if (!this._isCurrentStyle(map, styleVersion)) return
            await this.refreshAuthorizationToken()
            if (!this._isCurrentStyle(map, styleVersion)) return
            await this._setRasterSourceData(this.rasterImages, map, styleVersion)
        } catch (error) {
            if (!this._isCurrentStyle(map, styleVersion)) return
            if (!this._isStyleLoadingError(error)) throw error
        } finally {
            this._restoringStyleFeatures = false
            const retryMap = this._map
            const retryStyleVersion = this._styleVersion
            const styleFeaturesNeedRestore = this._styleFeaturesNeedRestore(retryMap, retryStyleVersion)
            const shouldRetry = allowRetry &&
                (this._styleRestorePending || styleFeaturesNeedRestore)
            this._styleRestorePending = false
            if (shouldRetry) {
                await this._restoreStyleFeatures(retryMap, retryStyleVersion, false)
            }
        }
    }

    public startListeners() {
        const initialMapFeatures = this.featureService.features.filter(feature => isMapFeature(feature))
        return this.mapFeatures$.pipe(
            startWith(initialMapFeatures)
        ).subscribe(features => {
            this._addFeaturesToMap(features)
        })
    }

    public removeDataGrid() {
        if (this._map.getLayer('grid-extrusion')) {
            this._map.removeLayer('grid-extrusion')
        }

        if (this._map.getSource('data-grid-source')) {
            this._deleteSource('data-grid-source')
        }
    }

    private _createDataGridSource() {
        this._map.addSource('data-grid-source', { type: 'geojson', data: { type: 'FeatureCollection', 'features': [] } })

        this._map.addLayer({
            id: 'grid-extrusion',
            type: 'fill-extrusion',
            source: 'data-grid-source',
            paint: {
                'fill-extrusion-height': ["get", "height"],
                'fill-extrusion-base': 0,
                'fill-extrusion-color': ["get", "color"],
                'fill-extrusion-opacity': 0.75
            }
        })

        this._map.on('click', 'grid-extrusion', (e) => {
            // Check that the feature exists
            if (e.features.length > 0) {
                const { lng, lat } = e.lngLat
                // Display the value in an alert or UI element
                const feature = e.features[0]
                const value = feature.properties.value

                this.popup = new mapboxgl.Popup({ closeOnClick: true, closeOnMove: true })
                    .setLngLat([lng, lat])
                    .setHTML(`<h1 class="m-0">${value}</h1>`)
                    .addTo(this._map)
            }
        })
    }

    private async _addFeaturesToMap(features: Feature[]) {
        const geoJsonFeatures = features.filter(feature => feature.type != 'rasterImage').map(feature => this._createGeoJsonFeature(feature)).filter(f => f != null)
        const rasterFeatures = features.filter(feature => feature.type == 'rasterImage')

        const lines = geoJsonFeatures.filter(({ geometry }) => geometry.type == 'LineString') as GeoJSON.Feature<GeoJSON.Point, GeoJSON.GeoJsonProperties>[]
        const markers = geoJsonFeatures.filter(({ geometry }) => geometry.type == 'Point') as GeoJSON.Feature<GeoJSON.Point, MarkerGeoJsonProperties>[]
        const polygon = geoJsonFeatures.filter(({ geometry }) => geometry.type == 'Polygon') as GeoJSON.Feature<GeoJSON.Point, GeoJSON.GeoJsonProperties>[]

        // Cache/install line and polygon data before awaiting marker sprites, auth, or raster work.
        // Those awaits can cross style events; deferring vectors can leave no later event to restore them.
        this._setSourceData(lines, 'line')
        this._setSourceData(polygon, 'polygon')
        this.restoreMapFeatureLayerOrder(this._map)

        await this._setMarkerSourceData(markers)
        await this.refreshAuthorizationToken()
        await this._setRasterSourceData(rasterFeatures, this._map, this._styleVersion)
    }

    /**
     * Converts a `Kartorium.Feature` to a `GeoJSON.Feature`.
     * @returns `GeoJSON.Feature`
     */
    private _createGeoJsonFeature(feature: Feature): GeoJSON.Feature {
        try {
            if (feature.type == 'line') {
                return this._createLineGeoJson(feature)
            } else if (feature.type == 'marker') {
                return this._createPointGeoJson(feature)
            } else if (feature.type == 'polygon') {
                return this._createPolygonGeoJson(feature)
            } else if (feature.type == 'rasterImage') {
                return this._createRasterImageGeoJson(feature)
            }
            console.warn(`Unhandled feature type '${feature.type}' for feature ${feature.id}`)
        } catch (err) {
            console.error(`Failed to create GeoJSON for feature ${feature.id} (${feature.name}):`, err)
            return null
        }
    }

    /**
    * Converts a **marker** `Kartorium.Feature` to a `GeoJSON.Feature`.
    * @returns `GeoJSON.Feature`
    */
    private _createPointGeoJson(feature: Feature): GeoJSON.Feature<GeoJSON.Point, MarkerGeoJsonProperties> {
        const backgroundColor = feature.backgroundColor?.value
        const backgroundShape = feature.backgroundShape?.value
        const displayType = feature.displayType?.value as MarkerTypes
        const fileReference = feature?.image?.fileReference
        const fontColor = feature.color?.value
        const icon = feature.icon?.value
        const isFilteredIn = this.filterService.isFilteredIn(feature)
        const isSelected = this.selectedFeatureID == feature.id
        const size = feature.size?.value ?? 0.06
        const pointGeometry = getPointGeometry(feature.position)
        const showText = displayType == 'label'
        const textContent = feature.text?.value

        if (displayType == 'icon') {
            const options = {
                backgroundColor: feature.backgroundColor?.value,
                backgroundShape: feature.backgroundShape?.value,
                color: feature.color?.value,
                icon: feature.icon?.value,
            } as TagOptions

            var imageName = "icon-" + simpleHash(JSON.stringify(options))
        } else if (displayType == 'image' && feature.image?.fileReference) {
            var imageName = feature.image.fileReference.hash
        } else if (displayType == 'image' && feature.image?.s3ObjectReferenceId) {
            var imageName = 's3-' + feature.image.s3ObjectReferenceId
        } else if (displayType == 'image') {
            var imageName = 'defaultMarker'
        } else if (displayType !== 'label') {
            // Fallback for markers without displayType (old data before displayType was added)
            var imageName = 'defaultMarker'
        }

        return {
            'id': feature.id?.toString(),
            'type': "Feature",
            'geometry': pointGeometry,
            'properties': {
                backgroundColor: backgroundColor,
                backgroundShape: backgroundShape,
                displayType: displayType,
                featureName: feature.name,
                fileReferenceID: fileReference?.id,
                fontColor: fontColor,
                hash: fileReference?.hash,
                icon: icon,
                imageName: imageName,
                interacted: isSelected,
                s3ObjectReferenceId: feature.image?.s3ObjectReferenceId,
                size: +size,
                opacity: feature.opacity,
                origin: 'karta',
                position: feature.position as any,
                showText: showText,
                textContent: textContent,
                type: 'marker',
                visibility: feature.visible && isFilteredIn, // the layer will filter features where visibility == false
            } as MarkerGeoJsonProperties
        } as GeoJSON.Feature<GeoJSON.Point, MarkerGeoJsonProperties>
    }

    /**
    * Converts a **line** `Kartorium.Feature` to a `GeoJSON.Feature`.
    * @returns `GeoJSON.Feature`
    */
    private _createLineGeoJson(feature: Feature): GeoJSON.Feature<GeoJSON.LineString, GeoJSON.GeoJsonProperties> {
        const isSelected = this.selectedFeatureID == feature.id
        const isFilteredIn = this.filterService.isFilteredIn(feature)
        const dashed: any = feature.properties?.find(p => p.key == 'dashed')?.value
        const coordinateString = feature.coordinateString?.value
        if (!coordinateString) return null
        const coordinates: number[][] = JSON.parse(coordinateString)
        const lineGeometry = getLineGeometry(coordinates)

        return {
            'id': feature.id.toString(),
            'type': "Feature",
            'geometry': lineGeometry,
            'properties': {
                type: 'line',
                origin: 'karta',
                interacted: isSelected,
                width: parseInt(feature.properties?.find(p => p.key == 'width')?.value) || 5,
                offset: parseInt(feature.properties?.find(p => p.key == 'offset')?.value) || 0,
                color: feature.properties?.find(p => p.key == 'color')?.value,
                dashed: dashed,
                visibility: feature.visible && isFilteredIn, // the layer will filter features where visibility == false
                opacity: feature.opacity,
            }
        } as GeoJSON.Feature<GeoJSON.LineString, GeoJSON.GeoJsonProperties>
    }

    /**
    * Converts a **polygon** `Kartorium.Feature` to a `GeoJSON.Feature`.
    * @returns `GeoJSON.Feature`
    */
    private _createPolygonGeoJson(feature: Feature): GeoJSON.Feature<GeoJSON.Polygon, GeoJSON.GeoJsonProperties> {
        const isSelected = this.selectedFeatureID == feature.id
        const isFilteredIn = this.filterService.isFilteredIn(feature)
        const outlineVisibility: any = feature.properties?.find(p => p.key == 'outlineVisibility')?.value
        const coordinateString = feature.coordinateString?.value

        if (!coordinateString) {
            return null
        }

        let coordinates: number[][] = JSON.parse(coordinateString)

        if (coordinates.length >= 3) {
            const firstPoint = coordinates[0]
            const lastPoint = coordinates[coordinates.length - 1]

            if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
                coordinates.push([...coordinates[0]])
            }
        }

        const polygonGeometry = getPolygonGeometry(coordinates)

        return {
            'id': feature.id.toString(),
            'type': "Feature",
            'geometry': polygonGeometry,
            'properties': {
                type: 'polygon',
                origin: 'karta',
                interacted: isSelected, // Might need to check onHover type for the line, poly and marker
                color: feature.properties?.find(p => p.key == 'color')?.value,
                outlineColor: feature.properties?.find(p => p.key == 'outlineColor')?.value,
                outlineVisibility: outlineVisibility,
                visibility: feature.visible && isFilteredIn,
                opacity: feature.opacity,
            }
        } as GeoJSON.Feature<GeoJSON.Polygon, GeoJSON.GeoJsonProperties>
    }

    /**
    * Converts a **rasterImage** `Kartorium.Feature` to a `GeoJSON.Feature`.
    * @returns `GeoJSON.Feature`
    */
    private _createRasterImageGeoJson(feature: Feature): GeoJSON.Feature<GeoJSON.Polygon, GeoJSON.GeoJsonProperties> {
        const isSelected = this.selectedFeatureID == feature.id
        const isFilteredIn = this.filterService.isFilteredIn(feature)
        const coordinateString = feature.coordinateString?.value
        if (!coordinateString) return null

        const coordinates: number[][] = JSON.parse(coordinateString)

        coordinates.push(coordinates[0]) // Connects the last point to the first point

        const polygonGeometry = getPolygonGeometry(coordinates)

        return {
            'id': feature.id.toString(),
            'type': "Feature",
            'geometry': polygonGeometry,
            'properties': {
                type: 'rasterImage',
                origin: 'karta',
                opacity: 0,
                color: '#ffffff',
                visibility: feature.visible && isFilteredIn,
                outlineVisibility: false,
                interacted: isSelected,
            }
        } as GeoJSON.Feature<GeoJSON.Polygon, GeoJSON.GeoJsonProperties>
    }

    private async _setMarkerSourceData(markers: GeoJSON.Feature<GeoJSON.Point, MarkerGeoJsonProperties>[], map = this._map, styleVersion = this._styleVersion) {
        if (this._map !== map) return
        const sourceID = 'pointSource'
        const prevMarkers = this.geoJsonSourceMap.get(sourceID) ?? []
        const prevImageNames = new Set<string>()

        // Collect previous image names
        prevMarkers.forEach(marker => prevImageNames.add(marker.properties.imageName))
        // Exclude image names from prevImageNames that are still in use
        markers.forEach(marker => prevImageNames.delete(marker.properties.imageName))

        // Remove unused images from the map's style
        prevImageNames.forEach(imageName => {
            if (imageName != null && map.hasImage(imageName)) {
                map.removeImage(imageName)
                this.markerImageMap.delete(imageName)
            }
        })

        this.geoJsonSourceMap.set(sourceID, markers)

        if (markers.length == 0 && this.styleLoaded) { // Delete source if there are no markers
            if (map.getLayer('pointLayer')) {
                map.removeLayer('pointLayer')
            }

            this._deleteSource(sourceID)
        } else { // Update source data or create source
            const data = { 'type': "FeatureCollection", 'features': markers } as GeoJSON.FeatureCollection

            await Promise.all(markers.map(marker => this._addImageForMarker(marker, map, styleVersion)))
            if (!this._isCurrentStyle(map, styleVersion)) return

            const mapSource = map.getSource(sourceID) as Mapbox.GeoJSONSource
            if (mapSource) {
                mapSource.setData(data)
                if (!map.getLayer('pointLayer')) this._createSymbolLayer()
            } else {
                const source = { type: 'geojson', data: data } as Mapbox.SourceSpecification

                this._addSourceToMap(sourceID, source, 'marker')
            }
        }
    }

    private _addImageForMarker(marker: GeoJSON.Feature<GeoJSON.Point, MarkerGeoJsonProperties>, map = this._map, styleVersion = this._styleVersion): Promise<HTMLImageElement | ImageBitmap> {
        if (marker.properties.displayType == 'icon') {
            const options = {
                backgroundColor: marker.properties.backgroundColor,
                backgroundShape: marker.properties.backgroundShape,
                color: marker.properties.fontColor,
                icon: marker.properties.icon,
            } as TagOptions
            const imageName = "icon-" + simpleHash(JSON.stringify(options))

            if (!map.hasImage(imageName)) {
                const svgString = createIconSVGString(options)
                if (svgString) {
                    return svgToCanvas(svgString)
                        .then(iconCanvas => iconCanvas.toDataURL())
                        .then(url => this._addImageToMap(url, imageName, map, styleVersion))
                        .catch(error => console.error(`Could not load Map image for ${marker.properties.featureName}`))
                } else {
                    // Icon SVG creation failed (invalid/missing icon) — load default marker under the same imageName
                    return this._addImageToMap('assets/BlueDot.png', imageName, map, styleVersion)
                }
            }
        } else if (marker.properties.displayType == 'image') {
            if (marker.properties.fileReferenceID) {
                const fileReferenceID = marker.properties.fileReferenceID
                const imageName = marker.properties.hash

                if (!map.hasImage(imageName)) {
                    return this.refreshAuthorizationToken().then(() => {
                        if (!this._isCurrentStyle(map, styleVersion)) return null
                        const projectID = this.projectService.currentProject.id
                        const url = this._fileReferenceService.getUrlForFileDownload(fileReferenceID, projectID, imageName)

                        return this._addImageToMap(url, imageName, map, styleVersion)
                            .catch(error => console.error(`Could not load Map icon for ${marker.properties.featureName}`))
                    })
                }
            } else if (marker.properties.s3ObjectReferenceId) {
                const imageName = 's3-' + marker.properties.s3ObjectReferenceId

                if (!map.hasImage(imageName)) {
                    return this._fileReferenceService.getS3FileUrlForProject(marker.properties.s3ObjectReferenceId)
                        .toPromise()
                        .then(url => this._addImageToMap(url, imageName, map, styleVersion))
                        .catch(error => console.error(`Could not load S3 Map icon for ${marker.properties.featureName}`))
                }
            } else {
                const imageName = 'defaultMarker'

                if (!map.hasImage(imageName)) {
                    const defaultMarkerURL = 'assets/BlueDot.png'

                    return this._addImageToMap(defaultMarkerURL, imageName, map, styleVersion)
                }
            }
        } else if (marker.properties.displayType !== 'label') {
            // Fallback for markers without displayType (old data)
            const imageName = 'defaultMarker'

            if (!map.hasImage(imageName)) {
                const defaultMarkerURL = 'assets/BlueDot.png'

                return this._addImageToMap(defaultMarkerURL, imageName, map, styleVersion)
            }
        }

        return Promise.resolve(null)
    }

    private _findGeoJsonSource(featureID: string) {
        for (let src of this.geoJsonSourceMap.keys()) {
            const features = this.geoJsonSourceMap.get(src)
            const foundFeature = features.find(f => String(f.id) === String(featureID))
            if (foundFeature) {
                return src
            }
        }
        return undefined
    }

    private _setSourceData(geoJsonFeatures: GeoJSON.Feature[], sourceID: string, type?: FeatureTypes) {
        if (geoJsonFeatures.length == 0 && this.styleLoaded) {
            this._deleteSource(sourceID)
        } else {
            const data = { 'type': "FeatureCollection", 'features': geoJsonFeatures } as GeoJSON.FeatureCollection
            const source = this._map.getSource(sourceID) as Mapbox.GeoJSONSource

            this.geoJsonSourceMap.set(sourceID, geoJsonFeatures)

            if (source) {
                source.setData(data)
            } else {
                const source = { type: 'geojson', data: data } as Mapbox.SourceSpecification

                this._addSourceToMap(sourceID, source, type ?? sourceID)
            }
        }
    }

    private async _setRasterSourceData(rasterImages: Feature[], map = this._map, styleVersion = this._styleVersion) {
        const request = {
            map,
            rasterImages: [...rasterImages],
            styleVersion,
        }
        if (!this._isCurrentStyle(map, styleVersion)) return
        if (this.settingRaster) {
            this._pendingRasterUpdate = request
            return
        }
        this.settingRaster = true

        try {
        // 1) Sort incoming rasterImages
        const sortedImages = [...rasterImages].sort((a, b) => {
            const orderA = a.properties?.find(prop => prop.key === "order")?.value
            const orderB = b.properties?.find(prop => prop.key === "order")?.value
            return (orderA ?? 0) - (orderB ?? 0)
        })

        // 2) Build a Set of the new source IDs that *should* exist
        const newSourceIDs = new Set(
            sortedImages.map(img => `tileset-${img.id}`)
        )

        // 3) Get all existing tileset layers and sources
        const style = map.getStyle()
        const existingTilesetIDs = new Set([
            ...style.layers.filter(layer => layer.id.startsWith('tileset-')).map(layer => layer.id),
            ...Object.keys(style.sources).filter(sourceID => sourceID.startsWith('tileset-')),
        ])

        // 4) Remove layers and sources that are NOT in the incoming rasterImages
        for (const sourceID of existingTilesetIDs) {
            if (!this._isCurrentStyle(map, styleVersion)) return
            if (!newSourceIDs.has(sourceID)) {
                if (map.getLayer(sourceID)) map.removeLayer(sourceID)
                if (map.getSource(sourceID)) map.removeSource(sourceID)
                this.rasterSourceMap.delete(sourceID)
            }
        }

        // 5) For each Feature in sortedImages, add or update the source/layer
        for (const rasterImage of sortedImages) {
            if (!this._isCurrentStyle(map, styleVersion)) return
            const sourceID = `tileset-${rasterImage.id}`
            const existingSource = map.getSource(sourceID) as Mapbox.RasterTileSource

            // Construct the tile URL (different if public project or not)
            const url = this.projectService.currentProject.publicStatus
                ? `${environment.api}/public/feature/tileset/${rasterImage.id}/{z}/{x}/{y}`
                : `${environment.api}/feature/tileset/${rasterImage.id}/{z}/{x}/{y}`

            if (!existingSource) {
                map.addSource(sourceID, {
                    type: 'raster',
                    tiles: [url],
                    tileSize: 256
                })
            }
            if (!this._isCurrentStyle(map, styleVersion)) return

            this.rasterSourceMap.set(sourceID, rasterImage)

            const layer = {
                id: sourceID,
                type: 'raster',
                source: sourceID,
                paint: {
                    'raster-fade-duration': 0,
                    'raster-opacity': rasterImage.opacity
                } as Mapbox.RasterLayerSpecification['paint'],
                filter: ['==', 'visibility', true],
                layout: {
                    'visibility': rasterImage.visible ? 'visible' : 'none'
                }
            } as Mapbox.RasterLayerSpecification

            if (!map.getLayer(sourceID)) {
                map.addLayer(layer, 'base-layer')
            } else {
                map.setPaintProperty(sourceID, 'raster-opacity', rasterImage.opacity)
                map.setLayoutProperty(
                    sourceID,
                    'visibility',
                    rasterImage.visible ? 'visible' : 'none'
                )
            }
        }
        } catch (error) {
            if (!this._isCurrentStyle(map, styleVersion)) return
            if (!this._isStyleLoadingError(error)) throw error
        } finally {
            this.settingRaster = false
            const pendingUpdate = this._pendingRasterUpdate
            this._pendingRasterUpdate = undefined
            if (pendingUpdate && this._isCurrentStyle(pendingUpdate.map, pendingUpdate.styleVersion)) {
                await this._setRasterSourceData(pendingUpdate.rasterImages, pendingUpdate.map, pendingUpdate.styleVersion)
            }
        }
    }

    public async refreshAuthorizationToken() {
        if (this.projectService.currentProject?.publicStatus) {
            this._authorizationToken = undefined
            return
        }

        this._authorizationToken = await this.authenticationService.getToken().toPromise()
    }

    private _updateSourceData(sourceID: string, features: GeoJSON.Feature[]) {
        const mapSource = (this._map.getSource(sourceID) as Mapbox.GeoJSONSource)
        const srcData = {
            'type': "FeatureCollection",
            'features': features
        } as GeoJSON.FeatureCollection<GeoJSON.Geometry>

        if (mapSource) {
            mapSource.setData(srcData)
        } else {
            const source = { type: 'geojson', data: srcData, } as Mapbox.SourceSpecification

            this._map.addSource(sourceID, source)
        }
    }

    private _deleteSource(sourceID: string) {
        const removeFromMap = () => {
            if (this._map.getLayer(sourceID))
                this._map.removeLayer(sourceID)

            if (sourceID == 'polygon' && this._map.getLayer('outlinepolygon'))
                this._map.removeLayer('outlinepolygon')

            if (sourceID && this._map.getSource(sourceID))
                this._map.removeSource(sourceID)

            if (this.geoJsonSourceMap.has(sourceID))
                this.geoJsonSourceMap.delete(sourceID)

            if (this.rasterSourceMap.has(sourceID))
                this.rasterSourceMap.delete(sourceID)

            if (sourceID && this._map.hasImage(sourceID))
                this._map.removeImage(sourceID)
        }

        if (this.styleLoaded) {
            removeFromMap()
        } else {
            this._map.once('style.load', () => removeFromMap())
        }
    }

    private _addSourceToMap(sourceID: string, source: Mapbox.SourceSpecification, type: string) {
        if (!this.styleLoaded) return

        try {
            this._map.addSource(sourceID, source)

            if (type == 'line') {
                this._createLineLayer()
            } else if (type == 'polygon') {
                this._createPolygonLayer()
            } else if (type == 'marker') {
                this._createSymbolLayer()
            }
        } catch (error) {
            if (!this._isStyleLoadingError(error)) throw error
        }
    }

    private _isStyleLoadingError(error: unknown) {
        return error instanceof Error && error.message.includes('Style is not done loading')
    }

    private _createSymbolLayer(extraFilters: any[] = []): Mapbox.SymbolLayer {
        let filters = ['==', 'visibility', true]

        // Combine the base filter with additional filters if provided
        if (extraFilters.length > 0) {
            filters = ['all', filters, ...extraFilters]
        }

        const overlap = !(this.sceneService.selectedScene?.overlap?.value ?? false)

        const layoutProperties = {
            'icon-allow-overlap': overlap,
            'icon-image': ['get', 'imageName'], // Use 'imageName' for point layers
            'icon-size': [
                'case',
                ['boolean', ['get', 'interacted'], false],
                ['case',
                    ['==', ['get', 'displayType'], 'image'],
                    ['*', 1.25, ['get', 'size']],
                    ['*', 10, ['get', 'size']]
                ],
                ['case',
                    ['==', ['get', 'displayType'], 'image'],
                    ['get', 'size'],
                    ['*', 8, ['get', 'size']]
                ]
            ],
            'text-allow-overlap': overlap,
            'text-anchor': 'center',
            'text-field': [
                'case',
                ['boolean', ['get', 'showText'], false], // Check the 'showText' property
                ['get', 'textContent'], // If true, use 'textContent'
                '' // If false, display nothing
            ],
            'text-letter-spacing': 0.1,
            'text-radial-offset': 2,
            'text-size': [
                'case',
                ['boolean', ['get', 'interacted'], false],
                ['*', 1.25, ['*', 100, ['get', 'size']]],
                ['*', 100, ['get', 'size']]
            ],
        } as Mapbox.SymbolLayerSpecification['layout']

        const paintProperties = {
            'icon-opacity': ['get', 'opacity'],
            'text-color': ['get', 'fontColor'],
            'text-halo-blur': 1,
            'text-halo-color': ['get', 'backgroundColor'],
            'text-halo-width': 1,
            'text-opacity': ['get', 'opacity'],
        } as Mapbox.SymbolLayerSpecification['paint']

        const layer = {
            'id': 'pointLayer',
            'source': 'pointSource', // reference the data source
            'filter': filters,
            'layout': layoutProperties,
            'paint': paintProperties,
            'type': 'symbol',
        } as Mapbox.SymbolLayerSpecification

        if (this._map.getLayer('pointLayer') == undefined) {
            this._map.addLayer(layer)
        }

        return layer
    }

    private _addImageToMap(url: string, imageName: string, map = this._map, styleVersion = this._styleVersion) {
        if (map.hasImage(imageName)) return Promise.resolve(null)
        const pendingLoad = this._markerImageLoadMap.get(imageName)
        if (pendingLoad?.map === map && pendingLoad.styleVersion === styleVersion) return pendingLoad.promise

        const imageUrl = this.markerImageMap.get(imageName) ?? url
        this.markerImageMap.set(imageName, imageUrl)
        const attemptKey = `${styleVersion}:${imageName}`
        this._markerImageRestoreAttempts.add(attemptKey)

        let load: Promise<HTMLImageElement | ImageBitmap | ImageData>
        load = new Promise<HTMLImageElement | ImageBitmap | ImageData>((resolve, reject) => {
            map.loadImage(imageUrl, (error, image) => {
                if (this._markerImageLoadMap.get(imageName)?.promise === load) {
                    this._markerImageLoadMap.delete(imageName)
                }
                if (error) {
                    if (this.markerImageMap.get(imageName) == imageUrl) {
                        this.markerImageMap.delete(imageName)
                    }
                    reject(error)
                    return
                }
                if (!this._isCurrentStyle(map, styleVersion)) {
                    // Ignore a sprite completed for an old style generation. The current generation's
                    // restoration path owns installing that image.
                    resolve(image)
                    return
                }
                try {
                    if (!map.hasImage(imageName)) {
                        map.addImage(imageName, image)
                    }
                    this._markerImageRestoreAttempts.delete(attemptKey)
                    resolve(image)
                } catch (addImageError) {
                    reject(addImageError)
                }
            })
        })
        this._markerImageLoadMap.set(imageName, { map, styleVersion, promise: load })

        const clearPendingLoad = () => {
            if (this._markerImageLoadMap.get(imageName)?.promise === load) this._markerImageLoadMap.delete(imageName)
        }
        void load.then(clearPendingLoad, clearPendingLoad)

        return load
    }

    private _createRasterImageLayer(sourceID: string) {
        const generateRasterLayer = (sourceID: string): Mapbox.RasterLayer => {
            const feature = this.rasterSourceMap.get(sourceID)

            return {
                'id': sourceID.toString(),
                'type': 'raster',
                'source': sourceID.toString(), // reference the data source
                'paint': {
                    'raster-fade-duration': 0,
                    "raster-opacity": feature.opacity,
                } as Mapbox.RasterLayerSpecification['paint'],
                'layout': {
                    'visibility': feature.visible ? 'visible' : 'none'
                } as Mapbox.RasterLayerSpecification['layout']
            } as Mapbox.RasterLayer
        }

        const layer = generateRasterLayer(sourceID)

        this._map.addLayer(layer, 'polygon')
    }

    private _createLineLayer() {
        const generateLineLayer = (sourceID: string): Mapbox.LineLayer => {
            return {
                'id': sourceID,
                'type': 'line',
                'source': sourceID, // reference the data source,
                'layout': {
                    'line-join': 'round',
                    'line-cap': 'round',
                } as Mapbox.LineLayout,
                'paint': {
                    'line-blur': 1,
                    'line-color': ['get', 'color'],
                    'line-offset': ['get', 'offset'],
                    'line-opacity': ['get', 'opacity'],
                    'line-width': ['case', ['boolean', ['get', 'interacted'], false], 10, ['get', 'width']],
                    'line-dasharray': ['case', ['boolean', ['get', 'dashed'], false], ['literal', [2, 2]], ['literal', [1, 0]]]
                } as Mapbox.LineLayerSpecification['paint'],
                'filter': ['==', 'visibility', true]
            } as Mapbox.LineLayerSpecification
        }

        let sourceID = 'line'
        let layer: Mapbox.LineLayerSpecification = generateLineLayer(sourceID)

        if (this._map.getLayer(sourceID) == null) {
            this._map.addLayer(layer)
        }
    }

    private _createPolygonLayer() {
        const generatePolygonLayer = (sourceID: string): Mapbox.FillLayer => {
            return {
                'id': sourceID,
                'type': 'fill',
                'source': sourceID, // reference the data source,
                'layout': {} as Mapbox.FillLayout,
                'paint': {
                    'fill-color': ['get', 'color'],
                    'fill-opacity': ['case', ['==', ['get', 'type'], 'rasterImage'], 0, ['case', ['boolean', ['get', 'interacted'], false], 0.9, ['get', 'opacity']]],
                } as Mapbox.FillPaint,
                'filter': ['==', 'visibility', true]
            } as Mapbox.FillLayer
        }

        /* Using a line layer here makes make it look nice */
        const generateOutlineLayer = (sourceID: string): Mapbox.LineLayer => {
            return {
                'id': 'outline' + sourceID,
                'type': 'line',
                'source': sourceID, // reference the data source,
                'layout': {
                    'line-join': 'round',
                    'line-cap': 'round',
                } as Mapbox.LineLayout,
                'paint': {
                    'line-blur': 1,
                    'line-color': ['get', 'outlineColor'],
                    'line-width': ['case', ['boolean', ['get', 'interacted'], false], 4, 3],
                } as Mapbox.LinePaint,
                /* only shows the outline if the polygon is visible && the outline is visible */
                'filter': ['==', ['all', ['boolean', ['get', 'visibility']], ['boolean', ['get', 'outlineVisibility']]], true],
            } as Mapbox.LineLayer
        }

        let sourceID = 'polygon'
        let polygonLayer: Mapbox.FillLayer = generatePolygonLayer(sourceID)
        let outlineLayer: Mapbox.LineLayer = generateOutlineLayer(sourceID)
        const beforeID = this._map.getLayer('3d-models') ? '3d-models' : undefined

        // Cached polygons may restore before the component re-adds 3d-models. Add without the
        // missing anchor; the later Viewer/Builder callback reapplies the final order.
        if (this._map.getLayer(sourceID) === undefined) this._map.addLayer(polygonLayer, beforeID)
        if (this._map.getLayer('outline' + sourceID) === undefined) this._map.addLayer(outlineLayer, beforeID)
    }

    public setInteracted(feature: Feature, interacted: boolean) {
        const featureID = feature.id.toString()

        if (feature.type == 'rasterImage') {
            const sourceID = `tileset-${feature.id}`

            this._map.setPaintProperty(sourceID, 'raster-contrast', interacted ? 0.5 : 0)
        } else {
            const sourceID = this._findGeoJsonSource(featureID)
            const geoJsonFeatures = this.geoJsonSourceMap.get(sourceID)
            const sourceFeature = geoJsonFeatures.find(data => data.id == featureID)

            sourceFeature.properties.interacted = interacted

            this._updateSourceData(sourceID, geoJsonFeatures)
        }
    }

    public dehighlightAll() {
        for (const sourceID of this.geoJsonSourceMap.keys()) {
            const sourceFeatures = this.geoJsonSourceMap.get(sourceID)

            sourceFeatures.forEach(feature => feature.properties.interacted = false)

            this._updateSourceData(sourceID, sourceFeatures)
        }
    }

    public isVisible(featureID: string) {
        const sourceID = this._findGeoJsonSource(featureID)

        return this.geoJsonSourceMap.get(sourceID).find(geoJSON => geoJSON.id == featureID).properties.visibility
    }

    /* Change visibility property of the feature rather than removing it from the source
    so we don't have to deal with the polygon outline layer as well */
    public hide(feature: Feature) {
        const featureID = feature.id.toString()

        if (feature.type == 'rasterImage') {
            const sourceID = `tileset-${feature.id}`

            this._map.setLayoutProperty(sourceID, 'visibility', 'none')
        } else {
            const sourceID = this._findGeoJsonSource(featureID)

            if (sourceID) {
                this.geoJsonSourceMap.get(sourceID).find(geoJSON => geoJSON.id == featureID).properties.visibility = false

                this._updateSourceData(sourceID, this.geoJsonSourceMap.get(sourceID))
            }
        }
    }

    /* Change visibility property of the feature rather than adding it back to the source
    so we don't have to deal with the polygon outline layer as well */
    public show(feature: Feature) {
        const featureID = feature.id.toString()

        if (feature.type == 'rasterImage') {
            const sourceID = `tileset-${feature.id}`

            this._map.setLayoutProperty(sourceID, 'visibility', 'visible')
        } else {
            const sourceID = this._findGeoJsonSource(featureID)

            if (sourceID) {
                this.geoJsonSourceMap.get(sourceID).find(geoJSON => geoJSON.id == featureID).properties.visibility = true

                this._updateSourceData(sourceID, this.geoJsonSourceMap.get(sourceID))
            }
        }
    }

    /* Change visibility property of the feature rather than adding it back to/removing it from the source
    so we don't have to deal with the polygon outline layer as well */
    public toggleVisibility(feature: Feature) {
        const featureID = feature.id.toString()

        if (feature.type == 'rasterImage') {
            const sourceID = `tileset-${feature.id}`

            this._map.setLayoutProperty(sourceID, 'visibility', feature.visible ? 'none' : 'visible')
        }

        const sourceID = this._findGeoJsonSource(featureID)

        if (sourceID) {
            const geoJSON = this.geoJsonSourceMap.get(sourceID).find(geoJSON => geoJSON.id == featureID)

            geoJSON.properties.visibility = !geoJSON.properties.visibility

            this._updateSourceData(sourceID, this.geoJsonSourceMap.get(sourceID))
        }
    }
}
